import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BellSlashIcon, XIcon, CaretRightIcon } from '@phosphor-icons/react';
import { usePushNotificationStatus } from '@/lib/hooks/usePushNotificationStatus';
import { SettingsContent } from '@/content/SettingsContent';

const DISMISS_STORAGE_KEY = 'notification-prompt-dismissed';

interface EnableNotificationsCardProps {
  /**
   * Whether to show the "Later" dismiss button
   * @default true
   */
  showLaterButton?: boolean;
}

/**
 * EnableNotificationsCard component displays a card prompting users to enable push notifications.
 * The card automatically hides if:
 * - Notifications are already enabled
 * - The user has dismissed it permanently (only when showLaterButton is true)
 * - Status is still loading
 *
 * On click of the primary button, navigates to the settings page to enable notifications.
 */
export function EnableNotificationsCard({
  showLaterButton = true,
}: EnableNotificationsCardProps) {
  const pushStatus = usePushNotificationStatus();
  const [isDismissed, setIsDismissed] = useState(
    () =>
      showLaterButton && localStorage.getItem(DISMISS_STORAGE_KEY) === 'true'
  );

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem(DISMISS_STORAGE_KEY, 'true');
    setIsDismissed(true);
  };

  // Don't show card if loading, enabled, or dismissed
  if (pushStatus === 'loading' || pushStatus === 'enabled' || isDismissed) {
    return null;
  }

  const content = SettingsContent.notificationPrompt;

  return (
    <Card className="bg-slate-600 mb-6">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex justify-between">
              <div className="flex gap-2 items-center mb-2">
                <BellSlashIcon
                  size={24}
                  weight="bold"
                  className="text-purple-300"
                />
                <span className="text-large">{content.title}</span>
              </div>
              {showLaterButton && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDismiss}
                  className="text-slate-400 hover:text-foreground bg-slate-700 rounded-full"
                  aria-label={content.dismissAriaLabel}
                >
                  <XIcon size={16} weight="bold" />
                </Button>
              )}
            </div>
            <p className="text-body text-slate-400 mb-3">
              {content.description}
            </p>
            <div className="flex items-center gap-3">
              <Link
                to="/account/settings"
                className="inline-flex items-center gap-1 h-9 rounded-md px-3 bg-purple-300 text-slate-600 text-body hover:bg-purple-400 transition-colors"
              >
                {content.enableButton}
                <CaretRightIcon size={14} weight="bold" />
              </Link>
              {showLaterButton && (
                <Button
                  variant="ghost"
                  onClick={handleDismiss}
                  className="text-slate-400 hover:text-foreground"
                >
                  {content.laterButton}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

